/** Wall time authoring without depending on the browser's current timezone. */
export function wallTime(instant: string, zone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(instant));
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}
export function wallTimeCandidates(value: string, zone: string): string[] {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return [];
  const base = Date.parse(`${value}:00Z`);
  if (!Number.isFinite(base)) return [];
  try {
    // IANA offsets fall within this range, including quarter-hour zones.
    const found: string[] = [];
    for (let minutes = -18 * 60; minutes <= 18 * 60; minutes += 15) {
      const candidate = new Date(base + minutes * 60_000).toISOString();
      if (wallTime(candidate, zone) === value) found.push(candidate);
    }
    return found;
  } catch { return []; }
}
export function resolveWallTime(value: string, zone: string, occurrence = ''): string {
  const candidates = wallTimeCandidates(value, zone);
  if (!candidates.length) throw new Error('This local time does not exist, or the timezone is invalid. Choose another time.');
  if (candidates.length > 1 && !candidates.includes(occurrence)) throw new Error('This time occurs twice when clocks change. Choose the intended UTC occurrence.');
  return candidates.length === 1 ? candidates[0]! : occurrence;
}
/** Entry requests intentionally do not trigger project-session redirects on 401. */
export class MeetingRequestError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}
export async function meetingRequest<T>(path: string, body?: unknown, method = body === undefined ? 'GET' : 'POST'): Promise<T> {
  const response = await fetch(`/api${path}`, { method, credentials: 'same-origin', ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }) });
  if (!response.ok) {
    const data = await response.json().catch(() => ({})) as { message?: string; error?: string };
    throw new MeetingRequestError(data.message || data.error || `Request failed (${response.status}).`, response.status);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}
export function meetingTimeLabel(instant: string, zone: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: zone }).format(new Date(instant));
}
